import { useEffect, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { Camera, Settings, Rocket, X, Globe, ChevronRight, LogOut, User as UserIcon, Flag, Sparkles, Monitor } from 'lucide-react'
import { useAppStore, useEventStore, useAuthStore } from '@/store'
import { useI18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'



export default function AdminLayout() {
  const { t } = useI18n()
  const location = useLocation()
  const sidebarOpen = useAppStore((s) => s.sidebarOpen)
  const setSidebarOpen = useAppStore((s) => s.setSidebarOpen)
  const locale = useAppStore((s) => s.locale)
  const setLocale = useAppStore((s) => s.setLocale)
  const { user, logout } = useAuthStore()
  const activeEventId = useEventStore((s) => s.activeEventId)
  const events = useEventStore((s) => s.events)
  const activeEvent = events.find((e) => e.id === activeEventId)

  const [openReports, setOpenReports] = useState(0)

  useEffect(() => {
    const load = () => {
      fetch('/api/reports/count')
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => data && setOpenReports(data.open))
        .catch(() => {})
    }
    load()
    // Polling is enough here — this is a low-urgency count, not something that
    // needs a live socket. 60s keeps it fresh without hammering the server.
    const interval = setInterval(load, 60000)
    return () => clearInterval(interval)
  }, [])

  const nav = [
    { path: '/admin', icon: Camera, label: t('nav.events'), exact: true },
    { path: '/admin/monitor', icon: Monitor, label: 'Live Booths' },
    { path: '/admin/poses', icon: Sparkles, label: 'Poses' },
    { path: '/admin/reports', icon: Flag, label: 'Reports', badge: openReports || undefined },
    { path: '/admin/settings', icon: Settings, label: t('nav.settings') },
  ]

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <aside
        className={cn(
          'flex flex-col border-r border-border bg-card/60 transition-all duration-300 shrink-0',
          sidebarOpen ? 'w-60' : 'w-16'
        )}
      >
        {/* Logo */}
        <div className={cn('flex h-14 items-center border-b border-border px-4 gap-3', !sidebarOpen && 'justify-center px-0')}>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/25">
            <Camera className="h-4 w-4 text-white" />
          </div>
          {sidebarOpen && (
            <span className="font-bold text-base tracking-tight">
              Pose<span className="text-primary">Box</span>
            </span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-0.5 p-2 pt-3">
          {nav.map((item) => {
            const active = item.exact
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path)
            return (
              <Link
                key={item.path}
                to={item.path}
                title={!sidebarOpen ? item.label : undefined}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  active
                    ? 'bg-primary/15 text-primary border border-primary/20'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  !sidebarOpen && 'justify-center px-2'
                )}
              >
                <span className="relative shrink-0">
                  <item.icon className="h-4.5 w-4.5" />
                  {!sidebarOpen && !!item.badge && (
                    <span className="absolute -right-1.5 -top-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500" />
                  )}
                </span>
                {sidebarOpen && (
                  <span className="flex flex-1 items-center justify-between">
                    {item.label}
                    {!!item.badge && (
                      <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                        {item.badge}
                      </span>
                    )}
                  </span>
                )}
              </Link>
            )
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-border p-2 space-y-2">
          {/* Active event + launch */}
          {activeEvent && sidebarOpen && (
            <div className="rounded-lg bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border border-emerald-500/20 p-3">
              <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-widest text-emerald-400/70">{t('nav.activeEvent')}</div>
              <div className="text-sm font-semibold truncate text-foreground">{activeEvent.name}</div>
              <a
                href={`/booth/${activeEvent.id}`}
                target="_blank"
                rel="noreferrer"
                className="mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-400 transition-colors shadow-lg shadow-emerald-500/25"
              >
                <Rocket className="h-3 w-3" />
                {t('editor.launchBooth')}
              </a>
            </div>
          )}

          {/* Locale selector */}
          {sidebarOpen && (
            <div className="flex items-center gap-2 rounded-md px-2 py-1.5">
              <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <select
                value={locale}
                onChange={(e) => setLocale(e.target.value as 'en' | 'nl')}
                className="flex-1 bg-transparent text-xs text-muted-foreground outline-none cursor-pointer pr-0"
                style={{ backgroundImage: 'none', paddingRight: 0 }}
              >
                <option value="en">English</option>
                <option value="nl">Nederlands</option>
              </select>
            </div>
          )}

          {user && (
            <div className={cn("border-t border-border p-2 space-y-1", !sidebarOpen && "items-center")}>
              <div className={cn("flex items-center gap-3 px-3 py-2 text-xs font-medium text-muted-foreground", !sidebarOpen && "justify-center px-0")}>
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
                  <UserIcon className="h-3 w-3" />
                </div>
                {sidebarOpen && <span className="truncate">{user.name}</span>}
              </div>
              <button
                onClick={() => logout()}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors",
                  !sidebarOpen && "justify-center px-0"
                )}
                title={!sidebarOpen ? 'Logout' : undefined}
              >
                <LogOut className="h-4 w-4 shrink-0" />
                {sidebarOpen && <span>Logout</span>}
              </button>
            </div>
          )}

          {/* Toggle button */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex w-full items-center justify-center gap-2 rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors text-xs"
            title={sidebarOpen ? t('nav.collapse') : t('nav.expand')}
          >
            {sidebarOpen ? (
              <>
                <X className="h-4 w-4" />
                <span>{t('nav.collapse')}</span>
              </>
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  )
}
